import { Fragment, useState } from 'react'
import classNames from 'classnames'
import { isEmpty } from 'lodash'
import Skeleton from 'react-loading-skeleton'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'

import { withStyles } from 'beam/dist/styles'
import { Typography } from 'beam/dist/Typography'
import { Button } from 'beam/dist/Button'
import { Tooltip } from 'beam/dist/Tooltip'
import BeamIcon from 'beam/dist/BeamIcon'
import { Select } from 'beam/dist/Select'
import { Input } from 'beam/dist/Input'
import { MenuItem } from 'beam/dist/Menu'
import IconButton from 'beam/dist/IconButton'
import { msToHHMMSS } from 'beam/dist/utils/time'

import { CONTACT_TYPE } from './mediaPlayer.constants'
import MediaPlayerMultiMenu from './mediaPlayerMultiMenu'
import AgentProfileDialog from '../../AgentProfile'

import { authenticatedUserShape, PERMISSIONS, withAuthenticatedUser } from '../../../Permissions'
import { useAppSelector } from '../../../root.reducer'
import theme from 'beam/dist/themes/calabrio'
import { useLocalizedMoment } from '../../../moment/useLocalizedMoment'

const autoQaIdPrefix = 'App-Recordings-MediaPlayer-Header-'

const PLAYBACK_RATES = [0.5, 1, 1.5, 2, 2.5, 3]

const styles = theme => ({
  isExpanded: {
    '& $headerInfo': {
      flexBasis: '33%',
    },
    '& $headerControls': {
      flexBasis: '34%',
    },
    '& $headerActions': {
      flexBasis: '33%',
    },
  },
  header: {
    padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
    paddingRight: 0,
    background: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  headerInfo: {
    flex: '0 0 20%',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    '& > *': {
      marginRight: '1rem',
    },
  },
  headerControls: {
    flex: '0 0 60%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.common.white,
  },
  headerActions: {
    flex: '0 0 20%',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    color: 'rgba(255,255,255,0.8)',
  },
  playButton: {
    width: theme.spacing(7),
    height: theme.spacing(7),
  },
  times: {
    whiteSpace: 'nowrap',
  },
  playbackRate: {
    width: '56px',
    textAlign: 'right',
    '&:before': {
      display: 'none',
    },
    '&:after': {
      display: 'none',
    },
  },
  playbackRateColor: {
    color: theme.palette.common.white,
    right: 0,
  },
  playbackRateSelect: {
    '&&': {
      paddingRight: theme.spacing(3),
    },
  },
  disabledIconButton: {
    color: 'rgba(255,255,255,0.26) !important',
  },
  actionBtn: {
    color: 'inherit',
  },
  actionBtnDark: {
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.08)',
    },
  },
  leftIcon: {
    marginRight: theme.spacing(1),
  },
  accountButtonText: {
    textTransform: 'none',
  },
})

const MediaPlayerHeader = props => {
  const {
    t,
    classes,
    authenticatedUser,
    isExpanded,
    ccrid,
    contact,
    timezoneOverride,
    mediaPlaybackRate,
    isZoomed,
    isMediaPlaying,
    mediaCurrentTime,
    mediaTimeline: { duration },
    isMediaReady,
    role,
    onNextClick,
    onPrevClick,
    onPlaybackRateChange,
    onToggleMediaPlaying,
    onToggleZoom,
    onToggleExpand,
    onUpdateContact,
    onRoleChange,
    onClose,
    showExpandButton = true,
  } = props

  const {
    data: { contactType },
  } = contact

  const { training, hr } = contact.updating ? contact.updating : contact.data
  const { formatDateTime } = useLocalizedMoment()
  const [isAgentProfileOpen, setIsAgentProfileOpen] = useState(false)

  const isCisco = useAppSelector(state => state.app.theme.displayName) === 'Cisco'

  const isPrevDisabled = () => {
    const {
      nextAndPreviousContactIds: { previous },
    } = props

    return !previous
  }

  const isNextDisabled = () => {
    const {
      nextAndPreviousContactIds: { next },
    } = props

    return !next
  }

  const handleMarkForHRClick = () => {
    const {
      data: { hr, ...contactData },
    } = contact

    onUpdateContact({
      ...contactData,
      hr: !hr,
    })
  }

  const handleMarkForTrainingClick = () => {
    const {
      data: { training, ...contactData },
    } = contact

    onUpdateContact({
      ...contactData,
      training: !training,
    })
  }

  return (
    <div
      className={classNames(classes.header, {
        [classes.isExpanded]: isExpanded,
      })}
    >
      <div className={classes.headerTop}>
        <div className={classes.headerInfo}>
          <Typography variant="subtitle2" color="inherit" auto-qa-id={`${autoQaIdPrefix}contactIdLabel`} data-testid={`${autoQaIdPrefix}contactIdLabel`}>
            {`${t('contact')} ${ccrid}`}
          </Typography>

          {isExpanded && !isEmpty(contact.data) && (
            <Fragment>
              { authenticatedUser.hasAnyPermission([PERMISSIONS.REPORT_AUTHORING, PERMISSIONS.VIEW_BI_CONTENT]) &&
                (contact.loading ? (
                  <Skeleton width={120} />
                ) : (
                  <Tooltip title={t('profileCard')}>
                    <Button
                      onClick={() => setIsAgentProfileOpen(true)}
                      variant="text"
                      style={{ color: isCisco ? '#171717' : theme.palette.secondary.main }}
                      auto-qa-id={`${autoQaIdPrefix}agentProfileButton`}
                      data-testid={`${autoQaIdPrefix}agentProfileButton`}
                    >
                      <BeamIcon className={classes.leftIcon}>CardAccountDetails</BeamIcon>
                      <Typography variant="caption" color="inherit" className={classes.accountButtonText}>
                        {contact.data.agent.displayId}
                      </Typography>
                    </Button>
                  </Tooltip>
                ))}
              {contact.loading ? (
                <Skeleton width={150} />
              ) : (
                <Fragment>
                  <Typography variant="caption" color="inherit" auto-qa-id={`${autoQaIdPrefix}contactDateLabel`} data-testid={`${autoQaIdPrefix}contactDateLabel`}>
                    {formatDateTime(contact.data.startTime, 'L', timezoneOverride ? timezoneOverride : contact.data.tz)}
                  </Typography>
                  <Typography variant="caption" color="inherit" auto-qa-id={`${autoQaIdPrefix}contactTimeLabel`} data-testid={`${autoQaIdPrefix}contactTimeLabel`}>
                    {formatDateTime(contact.data.startTime, 'LT', timezoneOverride ? timezoneOverride : contact.data.tz)}
                  </Typography>
                </Fragment>
              )}
            </Fragment>
          )}
        </div>

        {(contactType === CONTACT_TYPE.SCREEN_ONLY || contactType === CONTACT_TYPE.CALL) && (
          <div className={classes.headerControls}>
            <Select
              classes={{
                root: classes.playbackRateColor,
                icon: classes.playbackRateColor,
                outlined: classes.playbackRateSelect,
              }}
              className={classes.playbackRate}
              value={mediaPlaybackRate}
              onChange={onPlaybackRateChange}
              input={<Input id="playbackRateInput" />}
              auto-qa-id={`${autoQaIdPrefix}playbackRateSelect`}
              data-testid={`${autoQaIdPrefix}playbackRateSelect`}
            >
              {PLAYBACK_RATES.map(speed => (
                <MenuItem key={speed} value={speed}>{`${speed}x`}</MenuItem>
              ))}
            </Select>
            <IconButton color="inherit" onClick={onToggleZoom} auto-qa-id={`${autoQaIdPrefix}toggleZoomButton`} data-testid={`${autoQaIdPrefix}toggleZoomButton`}>
              <BeamIcon>{isZoomed ? 'MagnifyMinusOutline' : 'MagnifyPlusOutline'}</BeamIcon>
            </IconButton>
            <IconButton
              aria-label={t('previousButton')}
              color="inherit"
              onClick={onPrevClick}
              disabled={isPrevDisabled()}
              classes={{ disabled: classes.disabledIconButton }}
              auto-qa-id={`${autoQaIdPrefix}previousButton`}
              data-testid={`${autoQaIdPrefix}previousButton`}
            >
              <BeamIcon>SkipPrevious</BeamIcon>
            </IconButton>
            <IconButton
              aria-label={t('playButton')}
              classes={{
                root: classes.playButton,
                disabled: classes.disabledIconButton,
              }}
              color="inherit"
              onClick={onToggleMediaPlaying}
              disabled={!isMediaReady}
              auto-qa-id={`${autoQaIdPrefix}playButton`}
              data-testid={`${autoQaIdPrefix}playButton`}
            >
              {isMediaPlaying ? <BeamIcon size={48}>PauseCircle</BeamIcon> : <BeamIcon size={48}>PlayCircle</BeamIcon>}
            </IconButton>
            <IconButton
              aria-label={t('nextButton')}
              color="inherit"
              onClick={onNextClick}
              disabled={isNextDisabled()}
              classes={{ disabled: classes.disabledIconButton }}
              auto-qa-id={`${autoQaIdPrefix}nextButton`}
              data-testid={`${autoQaIdPrefix}nextButton`}
            >
              <BeamIcon>SkipNext</BeamIcon>
            </IconButton>
            <Typography
              className={classes.times}
              color="inherit"
              variant="caption"
              auto-qa-id={`${autoQaIdPrefix}currentTimeLabel`}
              data-testid={`${autoQaIdPrefix}currentTimeLabel`}
            >
              {`${msToHHMMSS(mediaCurrentTime)} / ${msToHHMMSS(typeof duration === 'undefined' ? 0 : duration)}`}
            </Typography>
          </div>
        )}

        <div className={classes.headerActions}>
          <MediaPlayerMultiMenu
            role={role}
            markForTraining={training}
            markForHR={hr}
            onRoleChange={onRoleChange}
            onMarkForTrainingChange={handleMarkForTrainingClick}
            onMarkForHRChange={handleMarkForHRClick}
          />
          {showExpandButton && (
            <IconButton
              className={classNames(classes.actionBtn, classes.actionBtnDark)}
              key="toggleExpand"
              aria-label={t('toggleExpand')}
              onClick={onToggleExpand}
              auto-qa-id={`${autoQaIdPrefix}toggleExpand`}
              data-testid={`${autoQaIdPrefix}toggleExpand`}
            >
              <BeamIcon>{isExpanded ? 'ArrowCollapse' : 'ArrowExpand'}</BeamIcon>
            </IconButton>
          )}
          <IconButton
            aria-label={t('closeButton')}
            className={classNames(classes.actionBtn, classes.actionBtnDark)}
            key="close"
            onClick={onClose}
            auto-qa-id={`${autoQaIdPrefix}closeButton`}
            data-testid={`${autoQaIdPrefix}closeButton`}
          >
            <BeamIcon>{'Close'}</BeamIcon>
          </IconButton>
        </div>
      </div>
      {!contact.loading && !isEmpty(contact.data) && isAgentProfileOpen && (
        <AgentProfileDialog
          open={isAgentProfileOpen}
          onClose={() => setIsAgentProfileOpen(false)}
          agentId={contact.data.agent.agentId}
        />
      )}
    </div>
  )
}

MediaPlayerHeader.propTypes = {
  t: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  ccrid: PropTypes.number,
  contact: PropTypes.shape({
    data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    loading: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    updating: PropTypes.bool,
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  isMediaPlaying: PropTypes.bool.isRequired,
  isZoomed: PropTypes.bool,
  isMediaReady: PropTypes.bool,
  mediaTimeline: PropTypes.object.isRequired,
  timezoneOverride: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  authenticatedUser: authenticatedUserShape.isRequired,
  role: PropTypes.string,
  mediaCurrentTime: PropTypes.number,
  mediaPlaybackRate: PropTypes.number,
  nextAndPreviousContactIds: PropTypes.object,
  onNextClick: PropTypes.func.isRequired,
  onPrevClick: PropTypes.func.isRequired,
  onPlaybackRateChange: PropTypes.func.isRequired,
  onUpdateContact: PropTypes.func.isRequired,
  onToggleMediaPlaying: PropTypes.func.isRequired,
  onToggleZoom: PropTypes.func.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  onRoleChange: PropTypes.func.isRequired,
  onMarkForTrainingClick: PropTypes.func,
  onMarkForHRClick: PropTypes.func,
  showExpandButton: PropTypes.bool,
}

export default withStyles(styles, { withTheme: true })(withTranslation()(withAuthenticatedUser(MediaPlayerHeader)))
